import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { useField } from "@app/scripts/react-hooks/field";
import { useLocaleContext } from "@app/scripts/react-providers/locales";
import { Phone } from "@app/scripts/react-general-components/Phone";

import type { Address } from "@app/scripts/types";

const InputField = ({ field, label, id, type, autoComplete, name, className, required }) => (
  <div className={className}>
    <input
      type={type ?? "text"}
      className="_field__input"
      id={id}
      autoComplete={autoComplete}
      placeholder={label}
      name={name}
      required={required}
      {...field}
    />
    <label htmlFor={id} className="_field__label">
      {label}
    </label>
  </div>
);

type Props = {
  address?: Address;
  newAddress?: boolean;
  toggleOpen: () => void;
};

const decodeHtmlEntities = (str) => {
  let tempElement = document.createElement("div");
  tempElement.innerHTML = str;
  return tempElement.textContent;
};

export const AddressForm: React.FC<Props> = ({ address, toggleOpen, newAddress = false }) => {
  const { addresses: addressesLocale } = useLocaleContext();
  const firstName = useField({ initialValue: decodeHtmlEntities(address?.first_name ? address.first_name : '') });
  const lastName = useField({ initialValue: decodeHtmlEntities(address?.last_name ? address.last_name : '') });
  const company = useField({ initialValue: address?.company });
  const phone = useField({ initialValue: address?.phone });
  const address1 = useField({ initialValue: address?.address1 });
  const address2 = useField({ initialValue: address?.address2 });
  const city = useField({ initialValue: address?.city });
  const zip = useField({ initialValue: address?.zip });
  const state = useField({ initialValue: address?.province });

  const [countries, setCountries] = useState<{ country: string; provinces: string[] }[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<{ country: string; provinces: string[] } | null>(null);
  const [states, setStates] = useState<string[]>([]);
  const [addressForm, setAddressForm] = useState<HTMLFormElement | null>(null);

  const onCountryChange = useCallback(
    (e: React.ChangeEvent) => {
      const dataSelected = countries?.find((x) => x.country === e.target.value) || null;
      setSelectedCountry(dataSelected);
      setStates(dataSelected?.provinces || []);
    },
    [countries, setSelectedCountry]
  );

  const ref = useRef(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (Object.keys(ref?.current?.errors)?.length) {
        return false;
      }

      const newForm = ref?.current?.querySelector("form") as HTMLElement;

      newForm.querySelectorAll("[name]")?.forEach((input) => {
        const fieldToUpdate = addressForm?.querySelector(`[name="${input.getAttribute("name")}"]`);
        // if (fieldToUpdate?.type )
        if (fieldToUpdate?.type === "checkbox") {
          fieldToUpdate.setAttribute("checked", input.getAttribute("checked"));
        } else if (fieldToUpdate) {
          fieldToUpdate.value = input.value;
        }
      });

      addressForm?.submit();
    },
    [addressForm]
  );

  const handleClose = (e) => {
    e.preventDefault();
    toggleOpen?.();
  };

  useEffect(() => {
    const format = (opt: HTMLOptionElement) => ({
      country: opt.value,
      provinces: JSON.parse(opt?.dataset?.provinces || "[]")?.map((x) => x?.[0]) || [],
    });
    const formId = !newAddress ? "address_form_" + address?.id : "address_form_new";
    const onPageAddressForm = document.getElementById(formId);
    const countryOptselector = newAddress ? "#AddressCountryNew option" : `#AddressCountry_${address.id} option`;
    const countries = Array.from(onPageAddressForm?.querySelectorAll(countryOptselector) || []) as HTMLOptionElement[];
    const data = countries?.map(format);
    const dataSelected = data?.find((x) => x.country === address?.country) || null;

    setSelectedCountry(dataSelected);
    setCountries(data);
    setStates(dataSelected?.provinces || []);
    setAddressForm(onPageAddressForm);
  }, []);

  return (
    <div>
      <ui-form no-submit ref={ref}>
        <form className="edit-address__form" noValidate onSubmit={handleSubmit}>
          <div className="edit-address__form-outer-wrap">
            <div className="edit-address__form-wrap">
              <InputField
                className="_field half"
                id="edit_first_name_drawer"
                autoComplete="given-name"
                required
                label={addressesLocale?.first_name || "First Name"}
                name="address[first_name]"
                field={firstName}
              />

              <InputField
                className="_field half"
                id="edit_last_drawer"
                autoComplete="family-name"
                required
                label={addressesLocale?.last_name || "Last Name"}
                name="address[last_name]"
                field={lastName}
              />

              <InputField
                className="_field"
                id="edit_compnay_drawer"
                autoComplete="organization"
                label={addressesLocale?.company || "Company (optional)"}
                name="address[company]"
                field={company}
              />

              <Phone name="address[phone]" tag="address[phone]" inputId="edit-phone-1_drawer" />

              <InputField
                className="_field"
                id="edit_address1_drawer"
                required
                name="address[address1]"
                autoComplete="address-line1"
                label={addressesLocale?.address1 || "Address 1"}
                field={address1}
              />

              <InputField
                className="_field"
                id="edit_address2_drawer"
                autoComplete="address-line2"
                name="address[address2]"
                label={addressesLocale?.address2 || "Address 2 (optional)"}
                field={address2}
              />

              <InputField
                className="_field half"
                id="edit_city_drawer"
                required
                label={addressesLocale?.city || "City"}
                autoComplete="address-level2"
                name="address[city]"
                field={city}
              />

              <InputField
                className="_field half"
                id="edit_zip_drawer"
                label={addressesLocale?.zip || "Post Code"}
                required
                name="address[zip]"
                field={zip}
              />

              <div className="_field _field__select half">
                <select
                  className="select__select"
                  defaultValue={state?.value}
                  onChange={state.onChange}
                  name="address[province]"
                >
                  {newAddress && <option>State</option>}
                  {states?.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div className="_field _field__select half">
                <select
                  className="select__select"
                  value={selectedCountry?.country}
                  onChange={onCountryChange}
                  name="address[country]"
                >
                  {newAddress && <option>Country</option>}
                  {countries?.map((x) => (
                    <option key={x.country} value={x.country}>
                      {x.country}
                    </option>
                  ))}
                </select>
              </div>

              <label className="checkbox w-full">
                <input name="address[default]" type="checkbox" defaultChecked={address?.default} />
                <span className="check-bg"></span>
                <ui-icon name="icon-check-small" class="icon-checkmark" width="16" height="16"></ui-icon>
                <span aria-hidden="true" className="checkbox__label w-full" data-label="Make this my primary address">
                  Make this my primary address
                </span>
              </label>
            </div>
          </div>

          <div className="edit-address__form-buttons">
            <Button
              onClick={handleClose}
              label={addressesLocale?.cancel || "Cancel"}
              variant="secondary"
              color="outline"
            />
            <Button type="submit" label="Save" variant="secondary" />
          </div>
        </form>
      </ui-form>
    </div>
  );
};
